// Anchors the global .db-select__* react-select styles at the composition root.
import '../../steps/step2-column/db-select-theme.module.css';

import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { type FormEvent, useMemo, useRef, useState } from 'react';

import { useChartTheme } from '@revelio/replots';
import { Select } from '@revelio/ui';

import { AlertDialog } from '../../../../shared/components/alert-dialog';
import { useOnModalOpen } from '../../../../shared/hooks/useOnModalOpen';
import { DownRightArrowIcon } from '../../../../shared/ui/icons';
import {
  findFilterDefByField,
  getFilterDefId,
} from '../../api/columns-api-helpers';
import type {
  CustomFilter,
  CustomFilterCondition,
  CustomFilterConditionType,
  DataBuilderCustomColumn,
  DataBuilderCustomColumnStep,
  FilterOperator,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import modalStyles from '../../data-builder-modals.module.css';
import type {
  CustomFilterCategory,
  CustomFilterDefinition,
} from '../../datasets/deliverable-column-config';
import { DB_SELECT_CLASS_NAME_PREFIX } from '../../steps/step2-column/db-select-theme';
import {
  isConditionValid,
  serializeConditionDrafts,
} from '../custom-filters/custom-filter-condition-helpers';
import filterStyles from '../custom-filters/custom-filter-modal.module.css';
import { DateFilterValueInput } from '../custom-filters/DateFilterValueInput';
import {
  customFilterNestedFilterSelectCssVars,
  customFilterSelectCssVars,
} from '../custom-filters/disabled-input-overrides';
import { FilterModifierSelect } from '../custom-filters/FilterModifierSelect';
import { NestedFilterSelect } from '../custom-filters/NestedFilterSelect/NestedFilterSelect';
import { NumericFilterValueInput } from '../custom-filters/NumericFilterValueInput';
import { SelectionListFilterValueInput } from '../custom-filters/SelectionListFilterValueInput';
import { StringFilterValueInput } from '../custom-filters/StringFilterValueInput';
import {
  getOperatorSelectOptions,
  isNullaryOperator,
} from '../custom-filters/string-filter-operators';

type CustomColumnModalProps = {
  isOpen: boolean;
  onClose: () => void;
  availableFilters: CustomFilterDefinition[];
  availableFilterCategories?: CustomFilterCategory[];
  onSubmit: (column: DataBuilderCustomColumn) => void;
  existingColumn?: DataBuilderCustomColumn;
  existingColumnNames: string[];
};

type ConditionDraft = {
  id: string;
  type: CustomFilterConditionType | null;
  filterDefId: string | null;
  field: string | null;
  groupKey: string | null;
  operator: FilterOperator | null;
  value: string[];
  modifier: string | null;
  selectionListItems?: SelectionListSelectedItem[];
};

type CustomColumnStepDraft = {
  id: string;
  conditions: ConditionDraft[];
  binary: 'AND' | 'OR' | null;
  then: string;
};

const createEmptyCondition = (): ConditionDraft => ({
  id: crypto.randomUUID(),
  type: null,
  filterDefId: null,
  field: null,
  groupKey: null,
  operator: null,
  value: [],
  modifier: null,
});

const createEmptyStepDraft = (): CustomColumnStepDraft => ({
  id: crypto.randomUUID(),
  conditions: [createEmptyCondition()],
  binary: null,
  then: '',
});

function getDefaultModifier(
  def: CustomFilterDefinition | undefined
): string | null {
  return def?.modifiers[0]?.key ?? null;
}

function getDefaultOperator(
  def: CustomFilterDefinition | undefined,
  operator: FilterOperator | null = null
): FilterOperator | null {
  if (def === undefined) {
    return operator;
  }

  const operators = def.operators as FilterOperator[];
  if (operator !== null && operators.includes(operator)) {
    return operator;
  }
  return operators[0] ?? null;
}

function initConditions(
  existing: CustomFilter | null,
  availableFilters: CustomFilterDefinition[]
): ConditionDraft[] {
  if (existing !== null) {
    return existing.conditions.map((condition) => {
      const filterDef = findFilterDefByField(
        condition.field,
        availableFilters,
        condition.groupKey
      );
      const filterDefId = filterDef
        ? getFilterDefId(filterDef)
        : condition.field;
      return {
        id: crypto.randomUUID(),
        type: condition.type ?? filterDef?.type ?? null,
        filterDefId,
        field: condition.field,
        groupKey: condition.groupKey ?? filterDef?.groupKey ?? null,
        operator: getDefaultOperator(filterDef, condition.operator),
        value: [...condition.value],
        modifier: condition.modifier ?? filterDef?.modifiers[0]?.key ?? null,
        selectionListItems: condition.selectionListItems
          ? [...condition.selectionListItems]
          : undefined,
      };
    });
  }
  return [createEmptyCondition()];
}

function serializeColumnState(
  currentColumnName: string,
  currentConditionalSteps: CustomColumnStepDraft[],
  currentElseValue: string
): string {
  return JSON.stringify({
    columnName: currentColumnName,
    elseValue: currentElseValue,
    conditionalSteps: currentConditionalSteps.map((step) => ({
      binary: step.binary,
      then: step.then,
      conditions: serializeConditionDrafts(step.conditions),
    })),
  });
}

const toDrafts = (
  existingColumn: DataBuilderCustomColumn | undefined,
  availableFilters: CustomFilterDefinition[]
): { conditionalSteps: CustomColumnStepDraft[]; elseValue: string } => {
  if (existingColumn === undefined || existingColumn.steps.length === 0) {
    return {
      conditionalSteps: [createEmptyStepDraft()],
      elseValue: '',
    };
  }

  const allButLast = existingColumn.steps.slice(0, -1);
  const lastStep = existingColumn.steps[existingColumn.steps.length - 1];

  if (lastStep === undefined || lastStep.case !== null) {
    return {
      conditionalSteps: existingColumn.steps.map((step) => ({
        id: crypto.randomUUID(),
        conditions: initConditions(step.case, availableFilters),
        binary: step.case?.binary ?? null,
        then: step.then,
      })),
      elseValue: '',
    };
  }

  return {
    conditionalSteps: allButLast.map((step) => ({
      id: crypto.randomUUID(),
      conditions: initConditions(step.case, availableFilters),
      binary: step.case?.binary ?? null,
      then: step.then,
    })),
    elseValue: lastStep.then,
  };
};

const createColumnName = (existingNames: string[]): string => {
  let index = existingNames.length + 1;
  let name = `custom_column_${index}`;
  while (existingNames.includes(name)) {
    index += 1;
    name = `custom_column_${index}`;
  }
  return name;
};

function getDisplayTextWidth(text: string, themed: boolean): number {
  if (text.length === 0) {
    return 100;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context === null) {
    return 100;
  }
  context.font = themed
    ? '600 23px/27px "Lay Grotesk", "Roboto", sans-serif'
    : '600 23px/27px "Source Sans Pro"';
  const width = context.measureText(text).width;
  return Math.max(width + 24, 100);
}

export function CustomColumnModal({
  isOpen,
  onClose,
  availableFilters,
  availableFilterCategories,
  onSubmit,
  existingColumn,
  existingColumnNames,
}: CustomColumnModalProps) {
  const [columnName, setColumnName] = useState('');
  const [conditionalSteps, setConditionalSteps] = useState<
    CustomColumnStepDraft[]
  >([createEmptyStepDraft()]);
  const [elseValue, setElseValue] = useState('');
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const initialStateRef = useRef('');
  const columnNameInputRef = useRef<HTMLInputElement | null>(null);
  const [modalContentEl, setModalContentEl] = useState<HTMLElement | null>(
    null
  );

  const categories = availableFilterCategories ?? [];
  const categorizedDefIds = new Set(
    categories.flatMap((cat) => cat.filters.map((d) => getFilterDefId(d)))
  );
  const topLevelDefs = availableFilters.filter(
    (d) => !categorizedDefIds.has(getFilterDefId(d))
  );

  const defsById = useMemo(
    () => new Map(availableFilters.map((def) => [getFilterDefId(def), def])),
    [availableFilters]
  );

  const getFilterDef = (
    defId: string | null
  ): CustomFilterDefinition | undefined =>
    defId === null ? undefined : defsById.get(defId);

  const getOperatorsForField = (defId: string | null): FilterOperator[] =>
    getFilterDef(defId)?.operators ?? [];

  const getValidConditions = (
    conditions: ConditionDraft[]
  ): CustomFilterCondition[] => {
    return conditions
      .filter((condition) => isConditionValid(condition, getFilterDef))
      .map((condition): CustomFilterCondition => {
        const filterDef = getFilterDef(condition.filterDefId);
        const groupKey = condition.groupKey ?? filterDef?.groupKey ?? null;
        if (
          filterDef?.type === 'selection_list' &&
          condition.selectionListItems
        ) {
          return {
            type: filterDef.type,
            field: condition.field as string,
            groupKey,
            operator: condition.operator as FilterOperator,
            value: condition.selectionListItems.map((item) => item.id),
            modifier: condition.modifier,
            selectionListItems: condition.selectionListItems,
          };
        }
        return {
          type: filterDef?.type ?? condition.type ?? 'string',
          field: condition.field as string,
          groupKey,
          operator: condition.operator as FilterOperator,
          value: condition.value,
          modifier: condition.modifier,
        };
      });
  };

  const getStepCaseFilter = (
    step: CustomColumnStepDraft
  ): CustomFilter | null => {
    const validConditions = getValidConditions(step.conditions);
    if (validConditions.length === 0) {
      return null;
    }
    return {
      id: crypto.randomUUID(),
      name: `case_${step.id}`,
      conditions: validConditions,
      binary: validConditions.length > 1 ? step.binary : null,
    };
  };

  const normalizeColumnSteps = (): DataBuilderCustomColumnStep[] => {
    const rules = conditionalSteps.reduce<DataBuilderCustomColumnStep[]>(
      (acc, step) => {
        const caseFilter = getStepCaseFilter(step);
        if (caseFilter === null || step.then.trim() === '') {
          return acc;
        }
        return [
          ...acc,
          {
            case: caseFilter,
            then: step.then.trim(),
          },
        ];
      },
      []
    );

    return [
      ...rules,
      {
        case: null,
        then: elseValue.trim(),
      },
    ];
  };

  const updateStep = (
    stepIndex: number,
    patch: Partial<CustomColumnStepDraft>
  ) => {
    setConditionalSteps((prev) =>
      prev.map((step, idx) =>
        idx === stepIndex ? { ...step, ...patch } : step
      )
    );
  };

  const updateCondition = (
    stepIndex: number,
    conditionIndex: number,
    patch: Partial<ConditionDraft>
  ) => {
    setConditionalSteps((prev) =>
      prev.map((step, sIdx) => {
        if (sIdx !== stepIndex) {
          return step;
        }
        return {
          ...step,
          conditions: step.conditions.map((condition, cIdx) =>
            cIdx === conditionIndex ? { ...condition, ...patch } : condition
          ),
        };
      })
    );
  };

  const addConditionToStep = (stepIndex: number) => {
    setConditionalSteps((prev) =>
      prev.map((step, idx) => {
        if (idx !== stepIndex) {
          return step;
        }
        const nextBinary = step.conditions.length === 1 ? 'AND' : step.binary;
        return {
          ...step,
          binary: nextBinary,
          conditions: [...step.conditions, createEmptyCondition()],
        };
      })
    );
  };

  const removeConditionFromStep = (
    stepIndex: number,
    conditionIndex: number
  ) => {
    setConditionalSteps((prev) =>
      prev.map((step, idx) => {
        if (idx !== stepIndex) {
          return step;
        }
        const nextConditions = step.conditions.filter(
          (_, cIdx) => cIdx !== conditionIndex
        );
        const normalizedConditions =
          nextConditions.length > 0 ? nextConditions : [createEmptyCondition()];
        const nextBinary =
          normalizedConditions.length <= 1 ? null : step.binary;
        return {
          ...step,
          binary: nextBinary,
          conditions: normalizedConditions,
        };
      })
    );
  };

  const renderValueInput = (
    filterDef: CustomFilterDefinition | undefined,
    condition: ConditionDraft,
    stepIndex: number,
    conditionIndex: number,
    required: boolean
  ) => {
    if (isNullaryOperator(condition.operator)) {
      return null;
    }

    if (filterDef?.type === 'selection_list') {
      return (
        <SelectionListFilterValueInput
          filterDef={filterDef}
          selectedItems={condition.selectionListItems ?? []}
          onChange={(items) => {
            updateCondition(stepIndex, conditionIndex, {
              selectionListItems: items,
              value: items.map((item) => item.id),
            });
          }}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }
    if (filterDef?.type === 'numeric') {
      return (
        <NumericFilterValueInput
          values={condition.value}
          onChange={(values) => {
            updateCondition(stepIndex, conditionIndex, { value: values });
          }}
          operator={condition.operator}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }
    if (filterDef?.type === 'date') {
      return (
        <DateFilterValueInput
          values={condition.value}
          onChange={(values) => {
            updateCondition(stepIndex, conditionIndex, { value: values });
          }}
          operator={condition.operator}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }
    if (filterDef?.type === 'boolean') {
      return null;
    }
    return (
      <StringFilterValueInput
        values={condition.value}
        onChange={(values) => {
          updateCondition(stepIndex, conditionIndex, { value: values });
        }}
        operator={condition.operator}
        isDisabled={condition.operator === null}
        required={required}
      />
    );
  };

  const isDuplicateName = (nextName: string): boolean => {
    const normalizedName = nextName.trim().toLowerCase();
    return existingColumnNames.some(
      (name) => name.trim().toLowerCase() === normalizedName
    );
  };

  const resetForm = () => {
    const drafts = toDrafts(existingColumn, availableFilters);
    const nextConditionalSteps =
      drafts.conditionalSteps.length > 0
        ? drafts.conditionalSteps
        : [createEmptyStepDraft()];
    const nextColumnName =
      existingColumn !== undefined
        ? existingColumn.name
        : createColumnName(existingColumnNames);

    initialStateRef.current = serializeColumnState(
      nextColumnName,
      nextConditionalSteps,
      drafts.elseValue
    );
    setConditionalSteps(nextConditionalSteps);
    setElseValue(drafts.elseValue);
    setColumnName(nextColumnName);
    setIsConfirmCloseOpen(false);
    columnNameInputRef.current?.setCustomValidity('');
  };

  const { isNewTheme } = useChartTheme();
  const nameWidth = useMemo(
    () => getDisplayTextWidth(columnName, isNewTheme),
    [columnName, isNewTheme]
  );

  useOnModalOpen(isOpen, () => {
    resetForm();
  });

  const addConditionStep = () => {
    setConditionalSteps((prev) => [...prev, createEmptyStepDraft()]);
  };

  const removeConditionStep = (stepIndex: number) => {
    setConditionalSteps((prev) => {
      const next = prev.filter((_, idx) => idx !== stepIndex);
      if (next.length > 0) {
        return next;
      }
      return [createEmptyStepDraft()];
    });
  };

  const isDirty =
    serializeColumnState(columnName, conditionalSteps, elseValue) !==
    initialStateRef.current;

  const requestClose = () => {
    if (isDirty) {
      setIsConfirmCloseOpen(true);
      return;
    }

    onClose();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isDuplicateName(columnName)) {
      columnNameInputRef.current?.setCustomValidity('Name must be unique.');
      columnNameInputRef.current?.reportValidity();
      return;
    }

    columnNameInputRef.current?.setCustomValidity('');

    if (elseValue.trim() === '') {
      return;
    }
    const steps = normalizeColumnSteps();
    if (steps.length < 2) {
      return;
    }
    const hasAtLeastOneRule = steps.some((step) => step.case !== null);
    if (!hasAtLeastOneRule) {
      return;
    }

    onSubmit({
      id: existingColumn?.id ?? crypto.randomUUID(),
      name: columnName.trim(),
      steps,
    });
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        size="2xl"
        isCentered
        closeOnOverlayClick
      >
        <ModalOverlay />
        <ModalContent
          className={modalStyles.modalContent}
          data-testid="data-builder-custom-column-modal-content"
        >
          <Box ref={setModalContentEl} as="form" onSubmit={handleSubmit}>
            <Box id="custom-column-name-input" pl="2" my="2" height="30px">
              <Flex alignItems="center">
                <Input
                  ref={columnNameInputRef}
                  value={columnName}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setColumnName(nextValue);
                    e.target.setCustomValidity('');
                  }}
                  fontWeight="600"
                  fontSize="lg"
                  pl="3"
                  height="30px"
                  variant="unstyled"
                  placeholder="Custom Name"
                  minW="100px"
                  width={nameWidth}
                  required
                  pattern="^[a-zA-Z0-9_]+$"
                  title="Name can only contain letters, numbers, and underscores"
                />
              </Flex>
            </Box>
            <Divider className={modalStyles.divider} />
            <ModalBody>
              {conditionalSteps.map((step, stepIndex) => {
                const caseLabel = stepIndex === 0 ? 'if' : 'else if';
                return (
                  <Box key={step.id}>
                    {stepIndex > 0 && (
                      <Divider
                        className={modalStyles.divider}
                        marginY="20px"
                        orientation="horizontal"
                      />
                    )}
                    <Flex>
                      <Text fontWeight="600" fontSize="md" mt="20px" mr="10px">
                        {caseLabel}
                      </Text>
                      <Box
                        className={modalStyles.modalSurfacePanel}
                        flexGrow="1"
                        pt="10px"
                        px="12px"
                      >
                        {step.conditions.map((condition, conditionIndex) => {
                          const isAddedCondition = conditionIndex > 0;
                          const showBinarySelect = conditionIndex === 1;
                          const filterDef = getFilterDef(condition.filterDefId);
                          const operatorOptions = getOperatorSelectOptions(
                            getOperatorsForField(condition.filterDefId)
                          );
                          const operatorValue =
                            condition.operator === null
                              ? null
                              : (operatorOptions.find(
                                  (option) =>
                                    option.value === condition.operator
                                ) ?? null);

                          return (
                            <Box key={condition.id}>
                              {isAddedCondition && (
                                <Flex marginY="20px" alignItems="center">
                                  <Divider
                                    className={modalStyles.divider}
                                    orientation="horizontal"
                                    borderBottomWidth="2px"
                                  />
                                  {showBinarySelect ? (
                                    <Select
                                      className={
                                        filterStyles.columnBinarySelect
                                      }
                                      size="sm"
                                      classNamePrefix={
                                        DB_SELECT_CLASS_NAME_PREFIX
                                      }
                                      value={
                                        step.binary === null
                                          ? undefined
                                          : {
                                              label: step.binary,
                                              value: step.binary,
                                            }
                                      }
                                      onChange={(option) => {
                                        const nextValue =
                                          option?.value === undefined
                                            ? null
                                            : (option.value as 'AND' | 'OR');
                                        updateStep(stepIndex, {
                                          binary: nextValue,
                                        });
                                      }}
                                      options={[
                                        { label: 'AND', value: 'AND' as const },
                                        { label: 'OR', value: 'OR' as const },
                                      ]}
                                      cssVars={customFilterSelectCssVars}
                                    />
                                  ) : (
                                    <Text fontSize="xs" px="10px">
                                      {step.binary}
                                    </Text>
                                  )}
                                  <Divider
                                    className={modalStyles.divider}
                                    orientation="horizontal"
                                    borderBottomWidth="2px"
                                  />
                                </Flex>
                              )}
                              <Flex justify="space-between" alignItems="center">
                                <Box w="180px">
                                  <NestedFilterSelect
                                    value={condition.filterDefId}
                                    selectedLabel={filterDef?.name ?? ''}
                                    onChange={(nextValue) => {
                                      const selectedDef =
                                        getFilterDef(nextValue);
                                      const nextField =
                                        selectedDef?.type === 'selection_list'
                                          ? selectedDef.selectionList
                                          : (selectedDef?.key ?? nextValue);
                                      updateCondition(
                                        stepIndex,
                                        conditionIndex,
                                        {
                                          type: selectedDef?.type ?? null,
                                          filterDefId: nextValue,
                                          field: nextField,
                                          groupKey:
                                            selectedDef?.groupKey ?? null,
                                          operator:
                                            getDefaultOperator(selectedDef),
                                          value: [],
                                          modifier:
                                            getDefaultModifier(selectedDef),
                                          selectionListItems: undefined,
                                        }
                                      );
                                    }}
                                    placeholder="Field"
                                    portalContainer={modalContentEl}
                                    cssVars={
                                      customFilterNestedFilterSelectCssVars
                                    }
                                    data-testid={`custom-column-field-select-${stepIndex}-${conditionIndex}`}
                                  >
                                    {topLevelDefs.map((def) => (
                                      <NestedFilterSelect.Item
                                        key={getFilterDefId(def)}
                                        value={getFilterDefId(def)}
                                        label={def.name}
                                      />
                                    ))}
                                    {categories.map((cat) => (
                                      <NestedFilterSelect.Group
                                        key={cat.key}
                                        label={cat.name}
                                      >
                                        {cat.filters.map((def) => (
                                          <NestedFilterSelect.Item
                                            key={getFilterDefId(def)}
                                            value={getFilterDefId(def)}
                                            label={def.name}
                                          />
                                        ))}
                                      </NestedFilterSelect.Group>
                                    ))}
                                  </NestedFilterSelect>
                                </Box>
                                {isAddedCondition && (
                                  <IconButton
                                    className={modalStyles.footerGhostButton}
                                    bg="none"
                                    aria-label="delete condition"
                                    icon={<CloseIcon boxSize={2.5} />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      removeConditionFromStep(
                                        stepIndex,
                                        conditionIndex
                                      )
                                    }
                                  />
                                )}
                              </Flex>

                              <Flex mt="10px" alignItems="flex-start">
                                <DownRightArrowIcon
                                  className={filterStyles.downRightIcon}
                                  mr="8px"
                                  mt="7px"
                                  ml="13px"
                                />
                                <Box w="143px" flexShrink={0}>
                                  <Select
                                    size="sm"
                                    classNamePrefix={
                                      DB_SELECT_CLASS_NAME_PREFIX
                                    }
                                    value={operatorValue}
                                    onChange={(option) => {
                                      updateCondition(
                                        stepIndex,
                                        conditionIndex,
                                        {
                                          operator:
                                            (option?.value as
                                              | FilterOperator
                                              | undefined) ?? null,
                                        }
                                      );
                                    }}
                                    options={operatorOptions}
                                    isDisabled={condition.filterDefId === null}
                                    placeholder="Operator"
                                    cssVars={customFilterSelectCssVars}
                                    required={condition.filterDefId !== null}
                                  />
                                </Box>
                                <Box flex={1} ml={2}>
                                  {renderValueInput(
                                    filterDef,
                                    condition,
                                    stepIndex,
                                    conditionIndex,
                                    true
                                  )}
                                </Box>
                              </Flex>

                              {filterDef !== undefined &&
                                filterDef.modifiers.length > 0 && (
                                  <Flex mt="10px" alignItems="center">
                                    <Box w="188px" flexShrink={0} />
                                    <Box width="100%">
                                      <FilterModifierSelect
                                        modifiers={filterDef.modifiers}
                                        value={condition.modifier}
                                        onChange={(modifier) => {
                                          updateCondition(
                                            stepIndex,
                                            conditionIndex,
                                            {
                                              modifier,
                                            }
                                          );
                                        }}
                                        isDisabled={condition.operator === null}
                                        testId={`custom-column-modifier-select-${stepIndex}-${conditionIndex}`}
                                      />
                                    </Box>
                                  </Flex>
                                )}
                            </Box>
                          );
                        })}
                        <Box width="100%" textAlign="right" pb="6px">
                          <Button
                            className={modalStyles.modalInkText}
                            colorScheme="lightBlue"
                            fontSize="xs"
                            fontWeight="600"
                            size="sm"
                            variant="link"
                            onClick={() => addConditionToStep(stepIndex)}
                          >
                            {step.binary === null
                              ? 'AND / OR'
                              : `+ ${step.binary}`}
                          </Button>
                        </Box>
                      </Box>
                    </Flex>
                    <Box pl="5">
                      <Flex alignItems="center" mt="10px">
                        <FormLabel
                          className={modalStyles.modalInkText}
                          fontSize="xs"
                          minWidth="60px"
                          color="text.primary"
                          mb="0"
                        >
                          Set value as
                        </FormLabel>
                        <Input
                          fontSize="xs"
                          value={step.then}
                          onChange={(e) =>
                            updateStep(stepIndex, { then: e.target.value })
                          }
                          placeholder="Category name"
                          data-testid={`custom-column-then-input-${stepIndex}`}
                          required
                        />
                        {stepIndex > 0 && (
                          <Button
                            className={modalStyles.footerResetLink}
                            colorScheme="red"
                            fontSize="12px"
                            fontWeight="600"
                            size="sm"
                            variant="link"
                            pl="6"
                            onClick={() => removeConditionStep(stepIndex)}
                          >
                            - Delete case
                          </Button>
                        )}
                      </Flex>
                    </Box>
                  </Box>
                );
              })}
              <Flex marginY="20px" alignItems="center">
                <Button
                  className={modalStyles.modalInkText}
                  colorScheme="lightBlue"
                  fontSize="12px"
                  fontWeight="600"
                  size="sm"
                  variant="link"
                  pr="6"
                  onClick={addConditionStep}
                >
                  + Add condition
                </Button>
                <Box flexGrow="1">
                  <Divider
                    className={modalStyles.divider}
                    orientation="horizontal"
                  />
                </Box>
              </Flex>
              <Text fontWeight="600" fontSize="md" mt="12px" mb="5px">
                else
              </Text>
              <Box id="else-condition-value" pl="5">
                <Box>
                  <Flex alignItems="center">
                    <FormLabel
                      className={modalStyles.modalInkText}
                      fontSize="xs"
                      minWidth="60px"
                      color="text.primary"
                      mb="0"
                    >
                      Set value as
                    </FormLabel>
                    <Input
                      fontSize="xs"
                      value={elseValue}
                      onChange={(e) => setElseValue(e.target.value)}
                      placeholder="Category name"
                      data-testid="custom-column-else-input"
                      required
                    />
                  </Flex>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter justifyContent="space-between" mt="45px">
              <Button
                className={modalStyles.footerResetLink}
                variant="link"
                colorScheme="red"
                size="sm"
                onClick={resetForm}
              >
                Reset
              </Button>
              <Flex gap={3}>
                <Button
                  className={modalStyles.footerGhostButton}
                  variant="ghost"
                  size="sm"
                  onClick={requestClose}
                >
                  Cancel
                </Button>
                <Button
                  className={modalStyles.footerPrimaryButton}
                  colorScheme="green"
                  size="sm"
                  type="submit"
                >
                  {existingColumn ? 'Edit Column' : 'Create Column'}
                </Button>
              </Flex>
            </ModalFooter>
          </Box>
        </ModalContent>
      </Modal>

      {isConfirmCloseOpen ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => {
            setIsConfirmCloseOpen(false);
          }}
          headerText="Discard Changes?"
          bodyContent={
            <span>
              You have unsaved changes. Do you want to continue without saving
              them?
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Discard"
          onAccept={() => {
            setIsConfirmCloseOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
